import { Box, Breadcrumbs, Button, CircularProgress, Link, List, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useParams, generatePath, useNavigate } from 'react-router'
import { sendGet, sendPut } from '../../hooks/use-fetch'
import * as AppRoutes from '../../Pages/Routes'
import { ChildInfoCard } from '../../Components/ChildDetails/ChildInfoCard'
import { LocationCardList } from '../../Components/Cards/MultiCardList/LocationCardList'
import { EnrollmentDetails } from '../../Components/Enrollment/EnrollmentDetails'
import CareNetworkDetails from '../../Components/CareNetwork/CareNetworkDetails'
import { ProgramVerification } from '../../Components/Program/ProgramVerification'
import { IncomeApprovalCommentsCard } from '../../Components/Cards/IncomeApproval/IncomeApprovalCommentsCard'
import { Modal } from '../../Components/Modal'
import { toast } from 'react-toastify'
import { DeclineApprovalDialog } from './DeclineApprovalDialog'
import { AccountType, type lookup, type postError } from '../../core/types'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { CheckListItem } from '../../Components/CheckList/CheckListItem'
import { TextAreaWithLabel } from '../../Components/TextArea'
import { useAccountId } from '../../ContextProviders/CurrentAccount'

export enum IncomeApprovalType {
  Income = 'Income',
  Waiver = 'Waiver'
}

export interface ApprovalDisplayData {
  id: number
  childID: number
  firstName: string
  middleName: string | null
  lastName: string
  suffix: string | null
  enrollmentID: number | null
  isApproved: boolean | null
  programVerificationID: number
  type: IncomeApprovalType
  programName: string
  verificationUser: string
  verificationUserEmail: string
  verificationUserServiceProvider: string | null
  comments: ApprovalComment[]
}

export interface ApprovalComment {
  type: string
  comment: string
  dateOf: Date
  userName: string
}

export function IncomeApprovalDetail (): JSX.Element {
  const { id } = useParams()
  const acct = useAccountId()
  const [approvalInfo, setApprovalInfo] = useState<ApprovalDisplayData | null>()
  const [approvalReason, setApprovalReason] = useState<number | null>(null)
  const [comment, setComment] = useState<string | null>(null)
  const [referralReasons, setReferralReasons] = useState<lookup[] | null>(null)
  const [showApproveConfirm, setShowApproveConfirm] = useState(false)
  const [showDeclineModel, setShowDeclineModel] = useState(false)
  const [refreshDate, setRefreshDate] = useState<Date>(new Date())
  const nav = useNavigate()

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (id !== undefined) {
        const { response: details } = await sendGet(`/IncomeApproval/ApprovalDisplayData?id=${id}`, {})
        if (details !== undefined) {
          setApprovalInfo(details)
        }
        const { response: refReasons } = await sendGet('/IncomeApproval/GetApprovalReasons', {})
        if (refReasons != null) { setReferralReasons(refReasons) }
      }
    }
    void fetchData()
  }, [refreshDate])

  const goHome = (): void => {
    nav('/')
  }

  const redirectPath = acct?.type === AccountType.SERVICE_PROVIDER ? AppRoutes.INCOME_APPROVALS : AppRoutes.ISD_INCOME_APPROVALS

  const goToIncomeApprovalList = (): void => {
    const detailsPage = generatePath(redirectPath)
    nav(detailsPage)
  }

  const goBack = (): void => {
    nav(-1)
  }

  const onDecline = (): void => {
    setShowDeclineModel(true)
  }

  const onCancel = (): void => {
    const detailsPage = generatePath(redirectPath)
    nav(detailsPage)
  }

  const onApprove = async (): Promise<void> => {
    setShowApproveConfirm(false)
    const submitData: { reasonId: number | null, responseNotes: string | null } = { reasonId: approvalReason, responseNotes: comment }

    const result = await sendPut(`/IncomeApproval/ApproveApproval?id=${id ?? '0'}`, submitData)
    const rsp: { newRecordID: number, success: boolean, errors: postError[] } = result.response

    if (rsp.success) {
      toast.success('The Income Approval was approved')
      setRefreshDate(new Date())
    } else {
      rsp.errors.forEach((e: postError) => {
        toast.error(e.error)
      })
    }
  }

  const onDeclineClose = (updated: boolean): void => {
    setShowDeclineModel(false)
    if (updated) {
      setRefreshDate(new Date())
    }
  }

  function openChildEdit (): void {
    if (approvalInfo !== undefined && approvalInfo !== null) {
      const editChild = `${generatePath(AppRoutes.CARENETWORK_MANAGEMENT_EDIT, { id: approvalInfo.childID.toString() ?? '0' })}?incomeApprovalID=${approvalInfo.id.toString() ?? '0'}`
      nav(editChild, { state: { redirectText: 'Income Approval Details' } })
    }
  }

  const handleReasonSelect = (id: number): void => {
    if (approvalReason === id) {
      setApprovalReason(null)
    } else {
      setApprovalReason(id)
    }
  }
  if (approvalInfo == null) {
    return <CircularProgress />
  } else {
    return <>
      <Typography variant="h3" data-testid="page-header">{approvalInfo.type} Approval for {approvalInfo.firstName} {approvalInfo.lastName}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <ArrowBackIcon onClick={goBack} sx={{ cursor: 'pointer' }} />
        <Breadcrumbs >
          <Link onClick={goHome} sx={{ textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>Home</Link>
          <Link onClick={goToIncomeApprovalList} sx={{ textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>Income Approvals</Link>
        </Breadcrumbs>
      </Box>

      <table style={{ margin: '0px auto' }} >
        <tr>
          <td className='card-grid-column'>
            {approvalInfo.enrollmentID != null &&
              <EnrollmentDetails enrollmentID={approvalInfo.enrollmentID} canReview={false} readOnly={true} />
            }
            <LocationCardList childID={approvalInfo.childID} readOnly={true} />
          </td>
          <td className='card-grid-column'>
            <ChildInfoCard id={approvalInfo.childID} onEdit={openChildEdit} />
            <CareNetworkDetails childId={approvalInfo.childID} readOnly={true} redirectText='Income Approval Details' />
          </td>
          <td className='card-grid-column'>
            {/* the key value triggers a reload when refreshDate changes */}
            <ProgramVerification verificationId={String(approvalInfo.programVerificationID)} doNotDisplayAsCard={true} readOnly={true} redirectText='Income Approval Details' key={refreshDate.toLocaleTimeString()} />
            <IncomeApprovalCommentsCard comments={approvalInfo.comments} />
          </td>
        </tr>
      </table>

      {
        approvalInfo.isApproved == null && <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: '10px', justifyContent: 'space-between' }}>
          <Button
            data-testid='declineButton'
            variant='contained'
            color='error'
            onClick={onDecline}
          > Decline </Button>
          <Button
            data-testid='cancelButton'
            variant='outlined'
            color='inherit'
            onClick={onCancel}
          > Cancel </Button>
          <Button
            data-testid='approveButton'
            variant='contained'
            onClick={() => { setShowApproveConfirm(true) }}
          > Approve</Button>
        </Box>
      }

      < Modal
        open={showApproveConfirm}
        onClose={() => { setShowApproveConfirm(false) }
        }
        onConfirm={onApprove}
        confirmationContent={<> Are you sure you want to approve this {approvalInfo.type} Approval for {approvalInfo.firstName} {approvalInfo.lastName} ?
          {approvalInfo.type === IncomeApprovalType.Waiver && (<>
            <Typography variant='h6' fontWeight={'bold'}>Approval Reason</Typography>
            <List
              sx={{
                width: '100%',
                maxWidth: '100%',
                bgcolor: 'transparent'
              }}
            >
              {referralReasons?.filter(r => r.id !== 4).map(r => {
                return <CheckListItem
                  dataTestId='reasonChecklist'
                  checked={approvalReason === r.id}
                  edge='start'
                  name={r.name}
                  text={r.name}
                  onClick={() => { handleReasonSelect(r.id) }}
                />
              })}
            </List>
          </>)}
          <TextAreaWithLabel label={'Comment'} name={'comment'}
            dataTestId='approvalComment'
            value={comment}
            onChange={function (e: any): void {
              setComment(e.target.value)
            }}
          />
        </>}
        title={`${approvalInfo.type} Approval`}
        confirmButtonText='Yes'
        cancelButtonText='No'
      />

      {showDeclineModel && <DeclineApprovalDialog approvalInfo={approvalInfo} onClose={onDeclineClose} />}
    </>
  }
}
